// src/components/dashboard/Leaderboards/Leaderboards.jsx

/*******************version-2-----using_CSS-Grid_and_not_Material-UI-Grid------responsive*********************************************************************************************************************************************************** */ 
import {
  Grid,
  Paper,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  alpha,
  Fade,
  Chip,
} from "@mui/material";
import {
  EmojiEvents,
  Stars,
  People,
  SportsEsports,
  TrendingUp,
} from "@mui/icons-material";
import TopWinners from "./TopWinners";
import TopByPoints from "./TopByPoints";
import TopPlayers from "./TopPlayers";
import TopByGames from "./TopByGames";

const Leaderboards = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const leaderboardSections = [
    {
      id: "topWinners",
      title: "Top 5 Winners",
      subtitle: "Highest earnings this period",
      component: TopWinners,
      icon: EmojiEvents,
      gradient: "linear-gradient(135deg, #FFD700 0%, #FFA000 100%)",
      iconColor: "#FFD700",
      count: "5 players",
    },
    {
      id: "topPlayers",
      title: "Top 5 Players",
      subtitle: "Most active participants",
      component: TopPlayers,
      icon: People,
      gradient: "linear-gradient(135deg, #2196F3 0%, #1565C0 100%)",
      iconColor: "#2196F3",
      count: "45 games",
    },
    {
      id: "topByGames",
      title: "Top 5 by Games",
      subtitle: "Game enthusiasts",
      component: TopByGames,
      icon: SportsEsports,
      gradient: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
      iconColor: "#4CAF50",
      count: "Active",
    },
    {
      id: "topByPoints",
      title: "Top 5 by Points",
      subtitle: "Most loyal players",
      component: TopByPoints,
      icon: Stars,
      gradient: "linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)",
      iconColor: "#9C27B0",
      count: "2.5K pts",
    },
  ];

  return (
    <Box sx={{ px: { xs: 2, sm: 4, md: 1 }, py: 4 }}>
      {/* Section Header */}
      <Fade in timeout={400}>
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 800,
              mb: 1,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: { xs: "1.75rem", sm: "2.25rem" },
              letterSpacing: "-0.02em",
            }}
          >
            Performance Leaderboards
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: { xs: "0.95rem", sm: "1.1rem" },
              fontWeight: 500,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Discover top performers and rising stars in our gaming community
          </Typography>
        </Box>
      </Fade>

      {/* FIXED: Properly responsive CSS Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr", // Mobile: 1 column
            sm: "1fr", // Small tablets: 1 column
            md: "repeat(2, 1fr)", // Medium and up: 2 columns  
            lg: "repeat(2, 1fr)", // Large: 2 columns
            xl: "repeat(2, 1fr)", // Extra large: 2 columns
          },
          gap: { 
            xs: 2, // Mobile: smaller gap
            sm: 3, // Small: medium gap  
            md: 4  // Desktop: larger gap
          },
          width: "100%",
          // Additional responsive adjustments
          gridAutoRows: "1fr", // Equal height rows
        }}
      >
        {leaderboardSections.map((section, index) => {
          const Component = section.component;
          const IconComponent = section.icon;

          return (
            <Fade
              key={section.id}
              in
              timeout={600}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  overflow: "hidden",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%",
                  minHeight: { xs: "auto", md: "400px" }, // Minimum height for desktop
                  background:
                    theme.palette.mode === "dark"
                      ? `linear-gradient(135deg, ${alpha(
                          theme.palette.background.paper,
                          0.8
                        )} 0%, ${alpha(theme.palette.grey[900], 0.9)} 100%)`
                      : `linear-gradient(135deg, ${
                          theme.palette.background.paper
                        } 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  position: "relative",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: { xs: "none", md: "translateY(-4px)" }, // Only hover effect on desktop
                    boxShadow: {
                      xs: "none",
                      md: `0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
                    },
                    "& .leaderboard-icon": {
                      transform: { xs: "none", md: "scale(1.1) rotate(5deg)" },
                    },
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 1,
                    background: section.gradient,
                    opacity: 0.6,
                  },
                }}
              >
                {/* Header - Made more responsive */}
                <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                      flexDirection: { xs: "column", sm: "row" }, // Stack on mobile
                      gap: { xs: 2, sm: 0 },
                    }}
                  >
                    <Box 
                      sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 2,
                        width: { xs: "100%", sm: "auto" },
                        justifyContent: { xs: "center", sm: "flex-start" },
                      }}
                    >
                      <Box
                        className="leaderboard-icon"
                        sx={{
                          p: { xs: 1, sm: 1.5 }, // Smaller padding on mobile
                          borderRadius: 3,
                          background: section.gradient,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: `0 6px 20px ${alpha(
                            section.iconColor,
                            0.3
                          )}`,
                          transition: "all 0.3s ease",
                        }}
                      >
                        <IconComponent
                          sx={{ 
                            color: "white", 
                            fontSize: { xs: "1.25rem", sm: "1.5rem" } // Smaller icon on mobile
                          }}
                        />
                      </Box>
                      <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "text.primary",
                            fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                            lineHeight: 1.2,
                          }}
                        >
                          {section.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ 
                            fontSize: { xs: "0.8rem", sm: "0.875rem" }, 
                            fontWeight: 500 
                          }}
                        >
                          {section.subtitle}
                        </Typography>
                      </Box>
                    </Box>

                    <Box 
                      sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 1,
                        justifyContent: { xs: "center", sm: "flex-end" },
                      }}
                    >
                      <Chip
                        label={section.count}
                        size="small"
                        sx={{
                          background: alpha(section.iconColor, 0.15),
                          color: section.iconColor,
                          fontWeight: 600,
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          height: { xs: 20, sm: 24 },
                          "& .MuiChip-label": { px: { xs: 1, sm: 1.5 } },
                        }}
                      />
                      <TrendingUp 
                        sx={{ 
                          color: "#4CAF50", 
                          fontSize: { xs: "0.9rem", sm: "1rem" } 
                        }} 
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Content - Better mobile spacing */}
                <Box
                  sx={{
                    px: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 3 },
                    flex: 1, // Takes remaining space
                    ...(isMobile && {
                      "& .MuiDataGrid-root": {
                        border: "none",
                        fontSize: "0.8rem", // Smaller text on mobile
                        "& .MuiDataGrid-virtualScroller": {
                          overflowX: "auto",
                          "&::-webkit-scrollbar": {
                            height: 6,
                          },
                          "&::-webkit-scrollbar-track": {
                            background: alpha(theme.palette.grey[300], 0.2),
                            borderRadius: 3,
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: alpha(theme.palette.primary.main, 0.3),
                            borderRadius: 3,
                            "&:hover": {
                              background: alpha(
                                theme.palette.primary.main,
                                0.5
                              ),
                            },
                          },
                        },
                      },
                    }),
                  }}
                  className={isMobile ? "mobile-scroll" : ""}
                >
                  <Component />
                </Box>
              </Paper>
            </Fade>
          );
        })}
      </Box>
    </Box>
  );
};

export default Leaderboards;
/*******************version-2-----using_CSS-Grid_and_not_Material-UI-Grid-none-responsive*********************************************************************************************************************************************************** */ 
// import React from "react";
// import {
//   Grid,
//   Paper,
//   Typography,
//   Box,
//   useMediaQuery,
//   useTheme,
//   alpha,
//   Fade,
//   Chip,
// } from "@mui/material";
// import {
//   EmojiEvents,
//   Stars,
//   People,
//   SportsEsports,
//   TrendingUp,
// } from "@mui/icons-material";
// import TopWinners from "./TopWinners";
// import TopByPoints from "./TopByPoints";
// import TopPlayers from "./TopPlayers";
// import TopByGames from "./TopByGames";

// const Leaderboards = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

//   const leaderboardSections = [
//     {
//       id: "topWinners",
//       title: "Top 5 Winners",
//       subtitle: "Highest earnings this period",
//       component: TopWinners,
//       icon: EmojiEvents,
//       gradient: "linear-gradient(135deg, #FFD700 0%, #FFA000 100%)",
//       iconColor: "#FFD700",
//       count: "5 players",
//     },

//     {
//       id: "topPlayers",
//       title: "Top 5 Players",
//       subtitle: "Most active participants",
//       component: TopPlayers,
//       icon: People,
//       gradient: "linear-gradient(135deg, #2196F3 0%, #1565C0 100%)",
//       iconColor: "#2196F3",
//       count: "45 games",
//     },
//     {
//       id: "topByGames",
//       title: "Top 5 by Games",
//       subtitle: "Game enthusiasts",
//       component: TopByGames,
//       icon: SportsEsports,
//       gradient: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
//       iconColor: "#4CAF50",
//       count: "Active",
//     },
//     {
//       id: "topByPoints",
//       title: "Top 5 by Points",
//       subtitle: "Most loyal players",
//       component: TopByPoints,
//       icon: Stars,
//       gradient: "linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)",
//       iconColor: "#9C27B0",
//       count: "2.5K pts",
//     },
//   ];

//   return (
//     <Box sx={{ px: { xs: 2, sm: 4, md: 1 }, py: 4 }}>
//       {/* Section Header */}
//       <Fade in timeout={400}>
//         <Box sx={{ mb: 4, textAlign: "center" }}>
//           <Typography
//             variant="h4"
//             component="h2"
//             sx={{
//               fontWeight: 800,
//               mb: 1,
//               background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
//               backgroundClip: "text",
//               WebkitBackgroundClip: "text",
//               WebkitTextFillColor: "transparent",
//               fontSize: { xs: "1.75rem", sm: "2.25rem" },
//               letterSpacing: "-0.02em",
//             }}
//           >
//             Performance Leaderboards
//           </Typography>
//           <Typography
//             variant="body1"
//             color="text.secondary"
//             sx={{
//               fontSize: { xs: "0.95rem", sm: "1.1rem" },
//               fontWeight: 500,
//               maxWidth: 600,
//               mx: "auto",
//             }}
//           >
//             Discover top performers and rising stars in our gaming community
//           </Typography>
//         </Box>
//       </Fade>

//       <Box
//         sx={{
//           display: "grid",
//           gridTemplateColumns: {
//             xs: "1fr",
//             md: "repeat(2, 1fr)",
//           },
//           gap: { xs: 3, md: 4 },
//           width: "100%",
//         }}
//       >
//         {leaderboardSections.map((section, index) => {
//           const Component = section.component;
//           const IconComponent = section.icon;

//           return (
//             <Fade
//               key={section.id}
//               in
//               timeout={600}
//               style={{ transitionDelay: `${index * 100}ms` }}
//             >
//               <Paper
//                 elevation={0}
//                 sx={{
//                   borderRadius: 4,
//                   overflow: "hidden",
//                   width: "100%",
//                   display: "flex",
//                   flexDirection: "column",
//                   justifyContent: "space-between",
//                   height: "100%",
//                   background:
//                     theme.palette.mode === "dark"
//                       ? `linear-gradient(135deg, ${alpha(
//                           theme.palette.background.paper,
//                           0.8
//                         )} 0%, ${alpha(theme.palette.grey[900], 0.9)} 100%)`
//                       : `linear-gradient(135deg, ${
//                           theme.palette.background.paper
//                         } 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
//                   border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
//                   position: "relative",
//                   transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
//                   "&:hover": {
//                     transform: "translateY(-4px)",
//                     boxShadow: `0 12px 40px ${alpha(
//                       theme.palette.common.black,
//                       0.15
//                     )}`,
//                     "& .leaderboard-icon": {
//                       transform: "scale(1.1) rotate(5deg)",
//                     },
//                   },
//                   "&::before": {
//                     content: '""',
//                     position: "absolute",
//                     top: 0,
//                     left: 0,
//                     right: 0,
//                     height: 1,
//                     background: section.gradient,
//                     opacity: 0.6,
//                   },
//                 }}
//               >
//                 <Box sx={{ p: 3, pb: 2 }}>
//                   <Box
//                     sx={{
//                       display: "flex",
//                       alignItems: "center",
//                       justifyContent: "space-between",
//                       mb: 2,
//                     }}
//                   >
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                       <Box
//                         className="leaderboard-icon"
//                         sx={{
//                           p: 1.5,
//                           borderRadius: 3,
//                           background: section.gradient,
//                           display: "flex",
//                           alignItems: "center",
//                           justifyContent: "center",
//                           boxShadow: `0 6px 20px ${alpha(
//                             section.iconColor,
//                             0.3
//                           )}`,
//                           transition: "all 0.3s ease",
//                         }}
//                       >
//                         <IconComponent
//                           sx={{ color: "white", fontSize: "1.5rem" }}
//                         />
//                       </Box>
//                       <Box>
//                         <Typography
//                           variant="h6"
//                           sx={{
//                             fontWeight: 700,
//                             color: "text.primary",
//                             fontSize: { xs: "1.1rem", sm: "1.25rem" },
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {section.title}
//                         </Typography>
//                         <Typography
//                           variant="body2"
//                           color="text.secondary"
//                           sx={{ fontSize: "0.875rem", fontWeight: 500 }}
//                         >
//                           {section.subtitle}
//                         </Typography>
//                       </Box>
//                     </Box>

//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <Chip
//                         label={section.count}
//                         size="small"
//                         sx={{
//                           background: alpha(section.iconColor, 0.15),
//                           color: section.iconColor,
//                           fontWeight: 600,
//                           fontSize: "0.75rem",
//                           height: 24,
//                           "& .MuiChip-label": { px: 1.5 },
//                         }}
//                       />
//                       <TrendingUp sx={{ color: "#4CAF50", fontSize: "1rem" }} />
//                     </Box>
//                   </Box>
//                 </Box>

//                 <Box
//                   sx={{
//                     px: 3,
//                     pb: 3,
//                     ...(isMobile && {
//                       "& .MuiDataGrid-root": {
//                         border: "none",
//                         "& .MuiDataGrid-virtualScroller": {
//                           overflowX: "auto",
//                           "&::-webkit-scrollbar": {
//                             height: 6,
//                           },
//                           "&::-webkit-scrollbar-track": {
//                             background: alpha(theme.palette.grey[300], 0.2),
//                             borderRadius: 3,
//                           },
//                           "&::-webkit-scrollbar-thumb": {
//                             background: alpha(theme.palette.primary.main, 0.3),
//                             borderRadius: 3,
//                             "&:hover": {
//                               background: alpha(
//                                 theme.palette.primary.main,
//                                 0.5
//                               ),
//                             },
//                           },
//                         },
//                       },
//                     }),
//                   }}
//                   className={isMobile ? "mobile-scroll" : ""}
//                 >
//                   <Component />
//                 </Box>
//               </Paper>
//             </Fade>
//           );
//         })}
//       </Box>
//     </Box>
//   );
// };

// export default Leaderboards;
/*******************************version-1*********************************************************************************************************************************************** */
// import React from "react";
// import {
//   Grid,
//   Paper,
//   Typography,
//   Box,
//   useMediaQuery,
//   useTheme,
//   alpha,
//   Fade,
//   Chip,
// } from "@mui/material";
// import {
//   EmojiEvents,
//   Stars,
//   People,
//   SportsEsports,
//   TrendingUp,
// } from "@mui/icons-material";
// import TopWinners from "./TopWinners";
// import TopByPoints from "./TopByPoints";
// import TopPlayers from "./TopPlayers";
// import TopByGames from "./TopByGames";

// const Leaderboards = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

//   const leaderboardSections = [
//     {
//       id: "topWinners",
//       title: "Top 5 Winners",
//       subtitle: "Highest earnings this period",
//       component: TopWinners,
//       icon: EmojiEvents,
//       gradient: "linear-gradient(135deg, #FFD700 0%, #FFA000 100%)",
//       iconColor: "#FFD700",
//       count: "5 players",
//     },

//     {
//       id: "topPlayers",
//       title: "Top 5 Players",
//       subtitle: "Most active participants",
//       component: TopPlayers,
//       icon: People,
//       gradient: "linear-gradient(135deg, #2196F3 0%, #1565C0 100%)",
//       iconColor: "#2196F3",
//       count: "45 games",
//     },
//     {
//       id: "topByGames",
//       title: "Top 5 by Games",
//       subtitle: "Game enthusiasts",
//       component: TopByGames,
//       icon: SportsEsports,
//       gradient: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
//       iconColor: "#4CAF50",
//       count: "Active",
//     },
//     {
//       id: "topByPoints",
//       title: "Top 5 by Points",
//       subtitle: "Most loyal players",
//       component: TopByPoints,
//       icon: Stars,
//       gradient: "linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)",
//       iconColor: "#9C27B0",
//       count: "2.5K pts",
//     },
//   ];

//   return (
//     <Box sx={{ px: { xs: 2, sm: 4, md: 1 }, py: 4 }}>
//       {/* Section Header */}
//       <Fade in timeout={400}>
//         <Box sx={{ mb: 4, textAlign: "center" }}>
//           <Typography
//             variant="h4"
//             component="h2"
//             sx={{
//               fontWeight: 800,
//               mb: 1,
//               background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
//               backgroundClip: "text",
//               WebkitBackgroundClip: "text",
//               WebkitTextFillColor: "transparent",
//               fontSize: { xs: "1.75rem", sm: "2.25rem" },
//               letterSpacing: "-0.02em",
//             }}
//           >
//             Performance Leaderboards
//           </Typography>
//           <Typography
//             variant="body1"
//             color="text.secondary"
//             sx={{
//               fontSize: { xs: "0.95rem", sm: "1.1rem" },
//               fontWeight: 500,
//               maxWidth: 600,
//               mx: "auto",
//             }}
//           >
//             Discover top performers and rising stars in our gaming community
//           </Typography>
//         </Box>
//       </Fade>

//       <Grid container  spacing={{ xs: 3, md: 4 }} alignItems="stretch">
//         {leaderboardSections.map((section, index) => {
//           const Component = section.component;
//           const IconComponent = section.icon;

//           return (
//             <Grid item xs={12} md={6} key={section.id} sx={{ display: "flex",  }}>
//               <Fade
//                 in
//                 timeout={600}
//                 style={{ transitionDelay: `${index * 100}ms` }}
//               >
//                 <Paper
//                   elevation={0}
//                   sx={{
//                     borderRadius: 4,
//                     overflow: "hidden",
//                     width: "100%",
//                     display: "flex",
//                     flexDirection: "column",
//                     justifyContent: "space-between",
//                     height: "100%",
//                     background:
//                       theme.palette.mode === "dark"
//                         ? `linear-gradient(135deg, ${alpha(
//                             theme.palette.background.paper,
//                             0.8
//                           )} 0%, ${alpha(theme.palette.grey[900], 0.9)} 100%)`
//                         : `linear-gradient(135deg, ${
//                             theme.palette.background.paper
//                           } 0%, ${alpha(
//                             theme.palette.primary.main,
//                             0.02
//                           )} 100%)`,
//                     border: `1px solid ${alpha(
//                       theme.palette.primary.main,
//                       0.1
//                     )}`,
//                     position: "relative",
//                     transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
//                     "&:hover": {
//                       transform: "translateY(-4px)",
//                       boxShadow: `0 12px 40px ${alpha(
//                         theme.palette.common.black,
//                         0.15
//                       )}`,
//                       "& .leaderboard-icon": {
//                         transform: "scale(1.1) rotate(5deg)",
//                       },
//                     },
//                     "&::before": {
//                       content: '""',
//                       position: "absolute",
//                       top: 0,
//                       left: 0,
//                       right: 0,
//                       height: 1,
//                       background: section.gradient,
//                       opacity: 0.6,
//                     },
//                   }}
//                 >
//                   {/* Header */}
//                   <Box sx={{ p: 3, pb: 2 }}>
//                     <Box
//                       sx={{
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "space-between",
//                         mb: 2,
//                       }}
//                     >
//                       <Box
//                         sx={{ display: "flex", alignItems: "center", gap: 2 }}
//                       >
//                         <Box
//                           className="leaderboard-icon"
//                           sx={{
//                             p: 1.5,
//                             borderRadius: 3,
//                             background: section.gradient,
//                             display: "flex",
//                             alignItems: "center",
//                             justifyContent: "center",
//                             boxShadow: `0 6px 20px ${alpha(
//                               section.iconColor,
//                               0.3
//                             )}`,
//                             transition: "all 0.3s ease",
//                           }}
//                         >
//                           <IconComponent
//                             sx={{ color: "white", fontSize: "1.5rem" }}
//                           />
//                         </Box>
//                         <Box>
//                           <Typography
//                             variant="h6"
//                             sx={{
//                               fontWeight: 700,
//                               color: "text.primary",
//                               fontSize: { xs: "1.1rem", sm: "1.25rem" },
//                               lineHeight: 1.2,
//                             }}
//                           >
//                             {section.title}
//                           </Typography>
//                           <Typography
//                             variant="body2"
//                             color="text.secondary"
//                             sx={{ fontSize: "0.875rem", fontWeight: 500 }}
//                           >
//                             {section.subtitle}
//                           </Typography>
//                         </Box>
//                       </Box>

//                       <Box
//                         sx={{ display: "flex", alignItems: "center", gap: 1 }}
//                       >
//                         <Chip
//                           label={section.count}
//                           size="small"
//                           sx={{
//                             background: alpha(section.iconColor, 0.15),
//                             color: section.iconColor,
//                             fontWeight: 600,
//                             fontSize: "0.75rem",
//                             height: 24,
//                             "& .MuiChip-label": { px: 1.5 },
//                           }}
//                         />
//                         <TrendingUp
//                           sx={{ color: "#4CAF50", fontSize: "1rem" }}
//                         />
//                       </Box>
//                     </Box>
//                   </Box>

//                   {/* Content */}
//                   <Box
//                     sx={{
//                       px: 3,
//                       pb: 3,
//                       ...(isMobile && {
//                         "& .MuiDataGrid-root": {
//                           border: "none",
//                           "& .MuiDataGrid-virtualScroller": {
//                             overflowX: "auto",
//                             "&::-webkit-scrollbar": {
//                               height: 6,
//                             },
//                             "&::-webkit-scrollbar-track": {
//                               background: alpha(theme.palette.grey[300], 0.2),
//                               borderRadius: 3,
//                             },
//                             "&::-webkit-scrollbar-thumb": {
//                               background: alpha(
//                                 theme.palette.primary.main,
//                                 0.3
//                               ),
//                               borderRadius: 3,
//                               "&:hover": {
//                                 background: alpha(
//                                   theme.palette.primary.main,
//                                   0.5
//                                 ),
//                               },
//                             },
//                           },
//                         },
//                       }),
//                     }}
//                     className={isMobile ? "mobile-scroll" : ""}
//                   >
//                     <Component />
//                   </Box>
//                 </Paper>
//               </Fade>
//             </Grid>
//           );
//         })}
//       </Grid>
//     </Box>
//   );
// };

// export default Leaderboards;

/*****************************************og-code************************************************************************************************************************************* */
// import React from 'react';
// import {
//   Grid,
//   Paper,
//   Typography,
//   Box,
//   useMediaQuery,
//   useTheme,
//   alpha,
//   Fade,
//   Chip
// } from '@mui/material';
// import {
//   EmojiEvents,
//   Stars,
//   People,
//   SportsEsports,
//   TrendingUp
// } from '@mui/icons-material';
// import TopWinners from './TopWinners';
// import TopByPoints from './TopByPoints';
// import TopPlayers from './TopPlayers';
// import TopByGames from './TopByGames';

// const Leaderboards = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));

//   const leaderboardSections = [
//     {
//       id: 'topWinners',
//       title: 'Top 5 Winners',
//       subtitle: 'Highest earnings this period',
//       component: TopWinners,
//       icon: EmojiEvents,
//       gradient: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)',
//       iconColor: '#FFD700',
//       count: '5 players',
//     },
//     {
//       id: 'topByPoints',
//       title: 'Top 5 by Points',
//       subtitle: 'Most loyal players',
//       component: TopByPoints,
//       icon: Stars,
//       gradient: 'linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)',
//       iconColor: '#9C27B0',
//       count: '2.5K pts',
//     },
//     {
//       id: 'topPlayers',
//       title: 'Top 5 Players',
//       subtitle: 'Most active participants',
//       component: TopPlayers,
//       icon: People,
//       gradient: 'linear-gradient(135deg, #2196F3 0%, #1565C0 100%)',
//       iconColor: '#2196F3',
//       count: '45 games',
//     },
//     {
//       id: 'topByGames',
//       title: 'Top 5 by Games',
//       subtitle: 'Game enthusiasts',
//       component: TopByGames,
//       icon: SportsEsports,
//       gradient: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
//       iconColor: '#4CAF50',
//       count: 'Active',
//     },
//   ];

//   return (
//     <Box>
//       {/* Section Header */}
//       <Fade in timeout={400}>
//         <Box sx={{ mb: 4, textAlign: 'center' }}>
//           <Typography
//             variant="h4"
//             component="h2"
//             sx={{
//               fontWeight: 800,
//               mb: 1,
//               background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
//               backgroundClip: 'text',
//               WebkitBackgroundClip: 'text',
//               WebkitTextFillColor: 'transparent',
//               fontSize: { xs: '1.75rem', sm: '2.25rem' },
//               letterSpacing: '-0.02em',
//             }}
//           >
//             Performance Leaderboards
//           </Typography>
//           <Typography
//             variant="body1"
//             color="text.secondary"
//             sx={{
//               fontSize: { xs: '0.95rem', sm: '1.1rem' },
//               fontWeight: 500,
//               maxWidth: 600,
//               mx: 'auto'
//             }}
//           >
//             Discover top performers and rising stars in our gaming community
//           </Typography>
//         </Box>
//       </Fade>

//       <Grid container spacing={{ xs: 3, md: 4 }}>
//         {leaderboardSections.map((section, index) => {
//           const Component = section.component;
//           const IconComponent = section.icon;

//           return (
//             <Grid item xs={12} md={6} key={section.id}>
//               <Fade in timeout={600} style={{ transitionDelay: `${index * 100}ms` }}>
//                 <Paper
//                   elevation={0}
//                   sx={{
//                     borderRadius: 4,
//                     overflow: 'hidden',
//                     height: 'fit-content',
//                     background: theme.palette.mode === 'dark'
//                       ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.grey[900], 0.9)} 100%)`
//                       : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
//                     border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
//                     position: 'relative',
//                     transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
//                     '&:hover': {
//                       transform: 'translateY(-4px)',
//                       boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
//                       '& .leaderboard-icon': {
//                         transform: 'scale(1.1) rotate(5deg)',
//                       }
//                     },
//                     '&::before': {
//                       content: '""',
//                       position: 'absolute',
//                       top: 0,
//                       left: 0,
//                       right: 0,
//                       height: 1,
//                       background: section.gradient,
//                       opacity: 0.6,
//                     }
//                   }}
//                 >
//                   {/* Header */}
//                   <Box sx={{ p: 3, pb: 2 }}>
//                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
//                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
//                         <Box
//                           className="leaderboard-icon"
//                           sx={{
//                             p: 1.5,
//                             borderRadius: 3,
//                             background: section.gradient,
//                             display: 'flex',
//                             alignItems: 'center',
//                             justifyContent: 'center',
//                             boxShadow: `0 6px 20px ${alpha(section.iconColor, 0.3)}`,
//                             transition: 'all 0.3s ease',
//                           }}
//                         >
//                           <IconComponent sx={{ color: 'white', fontSize: '1.5rem' }} />
//                         </Box>
//                         <Box>
//                           <Typography
//                             variant="h6"
//                             sx={{
//                               fontWeight: 700,
//                               color: 'text.primary',
//                               fontSize: { xs: '1.1rem', sm: '1.25rem' },
//                               lineHeight: 1.2,
//                             }}
//                           >
//                             {section.title}
//                           </Typography>
//                           <Typography
//                             variant="body2"
//                             color="text.secondary"
//                             sx={{ fontSize: '0.875rem', fontWeight: 500 }}
//                           >
//                             {section.subtitle}
//                           </Typography>
//                         </Box>
//                       </Box>

//                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                         <Chip
//                           label={section.count}
//                           size="small"
//                           sx={{
//                             background: alpha(section.iconColor, 0.15),
//                             color: section.iconColor,
//                             fontWeight: 600,
//                             fontSize: '0.75rem',
//                             height: 24,
//                             '& .MuiChip-label': { px: 1.5 },
//                           }}
//                         />
//                         <TrendingUp sx={{ color: '#4CAF50', fontSize: '1rem' }} />
//                       </Box>
//                     </Box>
//                   </Box>

//                   {/* Content */}
//                   <Box
//                     sx={{
//                       px: 3,
//                       pb: 3,
//                       ...(isMobile && {
//                         '& .MuiDataGrid-root': {
//                           border: 'none',
//                           '& .MuiDataGrid-virtualScroller': {
//                             overflowX: 'auto',
//                             '&::-webkit-scrollbar': {
//                               height: 6,
//                             },
//                             '&::-webkit-scrollbar-track': {
//                               background: alpha(theme.palette.grey[300], 0.2),
//                               borderRadius: 3,
//                             },
//                             '&::-webkit-scrollbar-thumb': {
//                               background: alpha(theme.palette.primary.main, 0.3),
//                               borderRadius: 3,
//                               '&:hover': {
//                                 background: alpha(theme.palette.primary.main, 0.5),
//                               },
//                             },
//                           },
//                         },
//                       }),
//                     }}
//                     className={isMobile ? 'mobile-scroll' : ''}
//                   >
//                     <Component />
//                   </Box>
//                 </Paper>
//               </Fade>
//             </Grid>
//           );
//         })}
//       </Grid>
//     </Box>
//   );
// };

// export default Leaderboards;
/****************************************************************************************************************************************************************************** */
// // src/components/dashboard/Leaderboards/Leaderboards.jsx
// import React from 'react';
// import { Grid, Paper, Typography, Box, useMediaQuery, useTheme } from '@mui/material';
// import TopWinners from './TopWinners';
// import TopByPoints from './TopByPoints';
// import TopPlayers from './TopPlayers';
// import TopByGames from './TopByGames';

// const Leaderboards = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));

//   const leaderboardSections = [
//     {
//       id: 'topWinners',
//       title: 'Top 5 Winners',
//       component: TopWinners,
//     },
//     {
//       id: 'topByPoints',
//       title: 'Top 5 by Points',
//       component: TopByPoints,
//     },
//     {
//       id: 'topPlayers',
//       title: 'Top 5 Players',
//       component: TopPlayers,
//     },
//     {
//       id: 'topByGames',
//       title: 'Top 5 by #Games',
//       component: TopByGames,
//     },
//   ];

//   return (
//     <Grid container spacing={3}>
//       {leaderboardSections.map((section) => {
//         const Component = section.component;

//         return (
//           <Grid item xs={12} md={6} key={section.id}>
//             <Paper
//               elevation={1}
//               sx={{
//                 p: 3,
//                 borderRadius: 2,
//                 height: 'fit-content',
//               }}
//             >
//               <Typography
//                 variant="h6"
//                 gutterBottom
//                 sx={{ fontWeight: 600, mb: 2 }}
//               >
//                 {section.title}
//               </Typography>

//               <Box
//                 sx={{
//                   ...(isMobile && {
//                     '& .MuiDataGrid-root': {
//                       '& .MuiDataGrid-virtualScroller': {
//                         overflowX: 'auto',
//                       },
//                     },
//                   }),
//                 }}
//                 className={isMobile ? 'mobile-scroll' : ''}
//               >
//                 <Component />
//               </Box>
//             </Paper>
//           </Grid>
//         );
//       })}
//     </Grid>
//   );
// };

// export default Leaderboards;
